import { Box } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboardingLens } from '../onboardingLens';
import { OnboardingFormMultiSelect } from '../OnboardingFormMultiSelect';
// CEE-867: additional notes field removed
// import { OnboardingFormTextArea } from '../OnboardingFormTextArea';
import { OnboardingStepHeader } from '../OnboardingStepHeader';
import { OnboardingStepScrollLayout } from '../OnboardingStepScrollLayout';
import {
  // ONBOARDING_MAX_ADDITIONAL_CONTEXT_LENGTH,
  ONBOARDING_MAX_MONTH_SELECTIONS,
  ONBOARDING_MONTH_OPTIONS,
} from '../onboardingConstants';

interface OnboardingStepScheduleAudienceProps {
  priorityMonths: string[];
  monthsToAvoid: string[];
  additionalContext: string;
  onPriorityMonthsChange: (value: string[]) => void;
  onMonthsToAvoidChange: (value: string[]) => void;
  onAdditionalContextChange: (value: string) => void;
  showErrors: boolean;
  fieldErrors: {
    priorityMonths?: string;
    monthsToAvoid?: string;
    additionalContext?: string;
  };
}

/** Figma Schedule and audience — priority months, months to avoid, additional context. */
export function OnboardingStepScheduleAudience({
  priorityMonths,
  monthsToAvoid,
  additionalContext: _additionalContext,
  onPriorityMonthsChange,
  onMonthsToAvoidChange,
  onAdditionalContextChange: _onAdditionalContextChange,
  showErrors,
  fieldErrors,
}: OnboardingStepScheduleAudienceProps) {
  const { t } = useTranslation();
  const { layout } = useOnboardingLens();

  const overlappingMonths = useMemo(
    () => priorityMonths.filter((month) => monthsToAvoid.includes(month)),
    [priorityMonths, monthsToAvoid],
  );

  // Resolve stale overlap (e.g. from persisted state) so Continue matches Figma filled state.
  useEffect(() => {
    if (overlappingMonths.length > 0) {
      onMonthsToAvoidChange(monthsToAvoid.filter((month) => !priorityMonths.includes(month)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once when step mounts
  }, []);

  const priorityMonthsError =
    showErrors && fieldErrors.priorityMonths
      ? t(`onboarding.wizard.step5.errors.${fieldErrors.priorityMonths}`, {
          max: ONBOARDING_MAX_MONTH_SELECTIONS,
        })
      : undefined;

  const monthsToAvoidError =
    showErrors && fieldErrors.monthsToAvoid
      ? t(`onboarding.wizard.step5.errors.${fieldErrors.monthsToAvoid}`, {
          max: ONBOARDING_MAX_MONTH_SELECTIONS,
        })
      : undefined;

  // CEE-867: additional notes field removed from onboarding schedule step
  // const additionalContextError =
  //   showErrors && fieldErrors.additionalContext
  //     ? t(`onboarding.wizard.step5.errors.${fieldErrors.additionalContext}`, {
  //         max: ONBOARDING_MAX_ADDITIONAL_CONTEXT_LENGTH,
  //       })
  //     : undefined;

  const handlePriorityMonthsChange = (value: string[]) => {
    onPriorityMonthsChange(value);
    onMonthsToAvoidChange(monthsToAvoid.filter((month) => !value.includes(month)));
  };

  const handleMonthsToAvoidChange = (value: string[]) => {
    onMonthsToAvoidChange(value);
    onPriorityMonthsChange(priorityMonths.filter((month) => !value.includes(month)));
  };

  return (
    <OnboardingStepScrollLayout
      px={layout.formSection.paddingX}
      py={layout.formSection.paddingY}
      headerGap={layout.formSection.sectionGap}
      header={
        <OnboardingStepHeader
          title={t('onboarding.wizard.step5.title')}
          subtitle={t('onboarding.wizard.step5.subtitle')}
          titleSubtitleGap={layout.formSection.headerSubtitleGap}
        />
      }
      bodySx={{
        display: 'flex',
        flexDirection: 'column',
        gap: layout.formSection.sectionGap,
        /** Room for focus ring without negative margins that clip the right column. */
        px: layout.focusRingGutter,
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: layout.dropdownRow.gap,
          width: '100%',
        }}
      >
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <OnboardingFormMultiSelect
            id="onboarding-priority-months"
            label={t('onboarding.wizard.step5.priority_months_label')}
            value={priorityMonths}
            options={ONBOARDING_MONTH_OPTIONS}
            onChange={handlePriorityMonthsChange}
            placeholder={t('onboarding.wizard.step5.multi_select_placeholder')}
            error={priorityMonthsError}
            maxSelections={ONBOARDING_MAX_MONTH_SELECTIONS}
            showCounter
            excludedValues={monthsToAvoid}
            required={false}
          />
        </Box>
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <OnboardingFormMultiSelect
            id="onboarding-months-to-avoid"
            label={t('onboarding.wizard.step5.months_to_avoid_label')}
            value={monthsToAvoid}
            options={ONBOARDING_MONTH_OPTIONS}
            onChange={handleMonthsToAvoidChange}
            placeholder={t('onboarding.wizard.step5.multi_select_placeholder')}
            error={monthsToAvoidError}
            maxSelections={ONBOARDING_MAX_MONTH_SELECTIONS}
            showCounter
            excludedValues={priorityMonths}
            required={false}
          />
        </Box>
      </Box>

      {/* CEE-867: additional notes field removed from onboarding schedule step
      <OnboardingFormTextArea
        id="onboarding-additional-context"
        label={t('onboarding.wizard.step5.additional_context_label')}
        value={additionalContext}
        onChange={onAdditionalContextChange}
        placeholder={t('onboarding.wizard.step5.additional_context_placeholder')}
        maxLength={ONBOARDING_MAX_ADDITIONAL_CONTEXT_LENGTH}
        error={additionalContextError}
      />
      */}
    </OnboardingStepScrollLayout>
  );
}
